import { useEffect } from 'react';

import { buildBrandThemeCss } from '../brand-theme';
import { useResolvedBranding } from './use-agency';

const STYLE_TAG_ID = 'agency-brand-theme';
const FAVICON_SELECTOR = 'link[rel="icon"]';

/**
 * Applies the §51-resolved white-label to the document chrome: the primary
 * color scale (a hue-rotated copy of the design-system ramp — see
 * brand-theme.ts), the agency favicon, and the tab title under full
 * white-label. Everything restores itself when the workspace resolves back
 * to default branding (e.g. after switching organizations).
 */
export function useApplyBranding(): void {
    const { data: branding } = useResolvedBranding();

    // Color scale — injected as a stylesheet so the `.dark` ramp scopes
    // correctly (inline custom properties cannot express the dark override).
    useEffect(() => {
        const css =
            branding?.source === 'agency' && branding.primaryColor
                ? buildBrandThemeCss(branding.primaryColor)
                : null;

        let styleTag = document.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null;
        if (!css) {
            styleTag?.remove();
            return;
        }
        if (!styleTag) {
            styleTag = document.createElement('style');
            styleTag.id = STYLE_TAG_ID;
            document.head.appendChild(styleTag);
        }
        styleTag.textContent = css;
        return () => {
            document.getElementById(STYLE_TAG_ID)?.remove();
        };
    }, [branding?.source, branding?.primaryColor]);

    // Favicon — swap every icon link to the agency favicon and restore the
    // originals on cleanup.
    useEffect(() => {
        if (!(branding?.source === 'agency' && branding.faviconUrl)) return;
        const links = [...document.querySelectorAll<HTMLLinkElement>(FAVICON_SELECTOR)];
        const originals = links.map((link) => link.href);
        for (const link of links) link.href = branding.faviconUrl;
        return () => {
            links.forEach((link, index) => {
                const original = originals[index];
                if (original) link.href = original;
            });
        };
    }, [branding?.source, branding?.faviconUrl]);

    // Tab title — only under full white-label (§41 "Hide AutoCampaign
    // branding"); otherwise the product title stays.
    useEffect(() => {
        if (
            !(
                branding?.source === 'agency' &&
                branding.hideAutocampaignBranding &&
                branding.brandName
            )
        ) {
            return;
        }
        const originalTitle = document.title;
        document.title = branding.brandName;
        return () => {
            document.title = originalTitle;
        };
    }, [branding?.source, branding?.hideAutocampaignBranding, branding?.brandName]);
}
